import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Card,
  Typography,
  Row,
  Col,
  Radio,
  Select,
  Space,
  message,
  Divider,
  DatePicker,
} from 'antd';
import { 
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
  TrophyOutlined,
  SafetyOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
import dayjs from 'dayjs';
import TrohyBadgeCard from '@/components/organisms/Card/TrohyBadgeCard';
import StudentSelectionModal from '@/components/Modal/StudentSelectionModal';
import StudentTable from '@/components/organisms/Table/StudentTable';
import { adminTrophyBadgeApi, adminStudentApi } from '@/services/api';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface TrophyBadgeMaster {
  id: number;
  name: string;
  description?: string;
  iconUrl?: string;
  type: 'trophy' | 'badge';
  grade?: number;
}

interface TrophyBadgeDetail {
  id: string;
  type: 'trophy' | 'badge';
  recipient: string;
  granter: string;
  grantDate: string;
  description: string;
  reason: string;
}

interface StudentData {
  id: string;
  name: string;
  email: string;
  grade: string;
  status: 'active' | 'inactive';
  registrationDate: string;
  academyCode: string;
  birthDate: string;
}

const AdminTrophyBadgeDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { trophyBadgeId } = useParams<{ trophyBadgeId: string }>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [trophyBadgeData, setTrophyBadgeData] = useState<TrophyBadgeDetail | null>(null);
  const [studentModalVisible, setStudentModalVisible] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState<StudentData[]>([]);
  const [trophyBadgeMasters, setTrophyBadgeMasters] = useState<TrophyBadgeMaster[]>([]);
  const [selectedType, setSelectedType] = useState<'trophy' | 'badge'>('trophy');
  const [selectedTrophyBadgeId, setSelectedTrophyBadgeId] = useState<number | null>(null);
  const [availableStudents, setAvailableStudents] = useState<StudentData[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const fromPath = (location.state as { from?: string } | null)?.from;

  // '/add' 경로에서는 params가 undefined이므로 확실하게 신규/수정 모드 구분
  const isEditMode = !!(trophyBadgeId && trophyBadgeId !== 'add');

  // 샘플 데이터 (수정 모드용)
  const sampleTrophyBadgeData: TrophyBadgeDetail = {
    id: 'tb-001',
    type: 'trophy',
    recipient: '김학생',
    granter: '이관리자',
    grantDate: '2024-01-15',
    description: '100일 연속 학습 달성',
    reason: '학습 의지가 뛰어나고 꾸준한 노력을 보여줌'
  };


  // 실제 학생 목록 로드
  const fetchStudents = async () => {
    setLoadingStudents(true);
    
    try {
      console.log('🔄 전체 학생 목록 로드 시작...');
      
      let allStudents: StudentData[] = [];
      let page = 0;
      let hasMore = true;
      
      // 모든 학생 데이터를 페이지별로 가져오기
      while (hasMore) {
        console.log(`📞 학생 API 호출: page=${page}`);
        const response = await adminStudentApi.listAll({ page, size: 200 });
        console.log(`📥 Page ${page} 응답:`, response);
        
        const data = response?.data || response;
        const students = data?.content || [];
        const totalElements = data?.totalElements || 0;
        
        console.log(`📚 Page ${page} - 학생 수:`, students.length, '/ 전체:', totalElements);
        
        if (students.length === 0) {
          hasMore = false;
          break;
        }
        
        const mapped = students
          .filter((s: any) => {
            const hasId = s.userId || s.id;
            if (!hasId) {
              console.warn('⚠️ ID 없는 학생 데이터:', s);
            }
            return hasId;
          })
          .map((s: any) => {
            const studentId = s.userId || s.id;
            return {
              id: String(studentId),
              name: s.name || '-',
              email: s.email || '-',
              grade: '-',
              status: 'active' as const,
              registrationDate: s.enrolledAt ? String(s.enrolledAt) : '-',
              academyCode: s.academyCode || '-',
              birthDate: s.birthDate ? String(s.birthDate) : '-'
            };
          });
        
        console.log(`✅ Page ${page} 매핑된 학생 수: ${mapped.length}`);
        allStudents = allStudents.concat(mapped);
        
        // 다음 페이지 확인
        if (allStudents.length >= totalElements || students.length < 200) {
          hasMore = false;
        } else {
          page++;
        }
      }
      
      // 최신 등록 순으로 정렬 (등록일시 내림차순)
      allStudents.sort((a, b) => {
        const ta = a.registrationDate && a.registrationDate !== '-' ? new Date(a.registrationDate).getTime() : 0;
        const tb = b.registrationDate && b.registrationDate !== '-' ? new Date(b.registrationDate).getTime() : 0;
        return tb - ta;
      });

      console.log('✅ 최종 로드된 학생 수:', allStudents.length);
      console.log('전체 학생 데이터:', allStudents);
      
      if (allStudents.length === 0) {
        message.info('등록된 학생이 없습니다.');
      } else {
        message.success(`${allStudents.length}명의 학생을 불러왔습니다.`);
      }
      
      setAvailableStudents(allStudents);
    } catch (error) {
      console.error('❌ 학생 로드 에러:', error);
      message.error('학생 목록을 불러오는데 실패했습니다.');
      setAvailableStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  };

  useEffect(() => {
    fetchTrophyBadgeMasters();
    // fetchStudents는 모달 열 때만 호출
    if (isEditMode) {
      fetchAwardDetail();
    }
    // 등록 모드일 때는 initialValue로 기본값 설정됨
  }, [trophyBadgeId, isEditMode]);

  const fetchTrophyBadgeMasters = async () => {
    try {
      const response = await adminTrophyBadgeApi.listMaster({});
      const data = response?.data || response;
      setTrophyBadgeMasters(data?.content || []);
    } catch (error) {
      message.error('트로피/뱃지 목록을 불러오는데 실패했습니다.');
      console.error(error);
    }
  };

  const fetchAwardDetail = async () => {
    if (!trophyBadgeId || trophyBadgeId === 'add') {
      console.log('⚠️ 잘못된 trophyBadgeId:', trophyBadgeId);
      return;
    }
    
    const numericId = Number(trophyBadgeId);
    if (isNaN(numericId)) {
      console.error('❌ trophyBadgeId를 숫자로 변환할 수 없습니다:', trophyBadgeId);
      message.error('잘못된 수여 ID입니다.');
      return;
    }
    
    setLoading(true);
    try {
      console.log('📥 수여 상세 정보 조회 - ID:', numericId);
      const response = await adminTrophyBadgeApi.getAwardDetail(numericId);
      const data = response?.data || response;
      console.log('📦 상세 데이터:', data);
      console.log('👤 학생 정보:', data.student);
      setTrophyBadgeData(data);
      form.setFieldsValue({
        type: data.trophyBadge?.type || 'trophy',
        trophyBadgeId: data.trophyBadge?.id,
        reason: data.reason,
        grantDate: dayjs(data.awardedAt)
      });
      setSelectedType(data.trophyBadge?.type || 'trophy');
      setSelectedTrophyBadgeId(data.trophyBadge?.id);
      // 수정 모드에서는 수여받은 학생 정보를 selectedStudents에 설정
      if (data.student) {
        const studentData: StudentData = {
          id: String(data.student.id),
          name: data.student.name || '-',
          email: data.student.email || '-',
          grade: '-',
          status: 'active' as const,
          registrationDate: '-',
          academyCode: '-',
          birthDate: '-'
        };
        console.log('✅ 설정된 학생 데이터:', studentData);
        setSelectedStudents([studentData]);
      } else {
        console.warn('⚠️ 학생 정보가 없습니다.');
      }
    } catch (error) {
      message.error('수여 정보를 불러오는데 실패했습니다.');
      console.error('❌ fetchAwardDetail 에러:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToList = () => {
    navigate(fromPath || '/admin/trophy-badge');
  };

  const handleSubmit = async (values: any) => {
    if (selectedStudents.length === 0) {
      message.warning('수여 대상 학생을 선택해주세요.');
      return;
    }

    setSaving(true);
    try {
      if (isEditMode) {
        // 수정 모드: 단일 수여 정보 수정
        const payload = {
          trophyBadgeId: values.trophyBadgeId,
          studentUserId: Number(selectedStudents[0].id),
          reason: values.reason,
          awardedAt: values.grantDate?.toISOString(),
        };

        const response = await adminTrophyBadgeApi.updateAward(Number(trophyBadgeId), payload);
        message.success('트로피/뱃지 수여 정보가 수정되었습니다.');
        navigate(fromPath || '/admin/trophy-badge');
      } else {
        // 등록 모드: 여러 명에게 수여
        const payload = {
          trophyBadgeId: values.trophyBadgeId,
          studentUserIds: selectedStudents.map((s: StudentData) => Number(s.id)),
          reason: values.reason,
          awardedAt: values.grantDate?.toISOString(),
        };

        const response = await adminTrophyBadgeApi.createAward(payload);
        const data = response?.data || response;
        
        const successCount = data?.results?.filter((r: any) => r.success)?.length || 0;
        const failCount = data?.results?.filter((r: any) => !r.success)?.length || 0;

        if (successCount > 0) {
          message.success(`${successCount}명에게 트로피/뱃지가 수여되었습니다.${failCount > 0 ? ` (${failCount}명 실패)` : ''}`);
        } else {
          message.error('트로피/뱃지 수여에 실패했습니다.');
        }
        
        navigate(fromPath || '/admin/trophy-badge');
      }
    } catch (error) {
      message.error('저장 중 오류가 발생했습니다.');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(fromPath || '/admin/trophy-badge');
  };

  const handleStudentModalOk = (newSelected: StudentData[]) => {
    if (newSelected.length > 0) {
      if (isEditMode) {
        // 수정 모드: 단일 학생으로 교체
        setSelectedStudents([newSelected[0]]);
        message.success('수여 대상자가 변경되었습니다.');
      } else {
        // 등록 모드: 여러 명 추가 (중복 제거)
        setSelectedStudents((prev) => {
          const map = new Map<string, StudentData>();
          prev.forEach(s => map.set(s.id, s));
          newSelected.forEach(s => map.set(s.id, s));
          return Array.from(map.values());
        });
        message.success(`${newSelected.length}명의 학생이 추가되었습니다.`);
      }
    }
    setStudentModalVisible(false);
  };

  const handleStudentModalCancel = () => {
    setStudentModalVisible(false);
  };

  const handleStudentModalOpen = async () => {
    setStudentModalVisible(true);
    // 모달을 열 때 학생 목록 로드
    if (availableStudents.length === 0) {
      console.log('🔍 모달 열기 - 학생 목록 로드 시작');
      await fetchStudents();
    } else {
      console.log('🔍 모달 열기 - 기존 학생 수:', availableStudents.length);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ padding: '24px', textAlign: 'center', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div>로딩 중...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <style>
        {`
          .trophy-badge-radio .ant-radio {
            display: none !important;
          }
          .trophy-badge-radio {
            margin: 0 !important;
          }
        `}
      </style>
      <div style={{ padding: '24px' }}>
        {/* 헤더 */}
        <div style={{ marginBottom: 24 }}>
          <Space style={{ marginBottom: 16 }}>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={handleBackToList}
            >
              목록으로
            </Button>
          </Space>
          <div className='flex justify-between items-center'>
            <Title level={2}>
              {isEditMode ? '트로피/뱃지 수여 상세' : '트로피/뱃지 수여'}
            </Title>
          </div>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
            <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="유형"
                name="type"
                initialValue="trophy"
                rules={[
                  { required: true, message: '유형을 선택해주세요' }
                ]}
              >
                <Radio.Group 
                  size="large"
                  onChange={(e) => {
                    setSelectedType(e.target.value);
                    setSelectedTrophyBadgeId(null);
                    form.setFieldValue('trophyBadgeId', null);
                  }}
                >
                  <Radio value="trophy">트로피</Radio>
                  <Radio value="badge">뱃지</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            label={
              <span style={{ fontSize: '16px', fontWeight: 600 }}>
                트로피 및 뱃지 선택
              </span>
            }
            name="trophyBadgeId"
            rules={[
                { required: true, message: '트로피 또는 뱃지를 선택해주세요' }
            ]}
          >
            <div style={{ marginTop: '16px' }}>
              {trophyBadgeMasters.filter(tb => tb.type === selectedType).length === 0 ? (
                <div style={{ 
                  padding: '40px', 
                  textAlign: 'center', 
                  color: '#999',
                  border: '1px dashed #d9d9d9',
                  borderRadius: '8px'
                }}>
                  선택 가능한 {selectedType === 'trophy' ? '트로피' : '뱃지'}가 없습니다
                </div>
              ) : (
                <Radio.Group 
                  className='w-full'
                  onChange={(e) => {
                    setSelectedTrophyBadgeId(e.target.value);
                    form.setFieldValue('trophyBadgeId', e.target.value);
                  }}
                  value={selectedTrophyBadgeId}
                >
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                    gap: '20px'
                  }}>
                    {trophyBadgeMasters
                      .filter(tb => tb.type === selectedType)
                      .sort((a, b) => (a.grade || 999) - (b.grade || 999))
                      .map(tb => {
                        const isSelected = selectedTrophyBadgeId === tb.id;
                        const gradeColors: { [key: number]: string } = {
                          1: '#FFD700', // Gold
                          2: '#C0C0C0', // Silver
                          3: '#CD7F32', // Bronze
                        };
                        const gradeBadgeColor = gradeColors[tb.grade || 0] || '#1890ff';
                        const selectionColor = '#1890ff'; // 통일된 선택 색상
                        
                        return (
                          <Radio 
                            key={tb.id} 
                            value={tb.id}
                            style={{ 
                              display: 'block', 
                              height: '100%',
                            }}
                            className='trophy-badge-radio'
                          >
                            <Card 
                              hoverable 
                              style={{ 
                                height: '100%',
                                textAlign: 'center',
                                border: isSelected ? `3px solid ${selectionColor}` : '1px solid #f0f0f0',
                                boxShadow: isSelected 
                                  ? `0 4px 12px ${selectionColor}40` 
                                  : '0 2px 8px rgba(0,0,0,0.08)',
                                transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer',
                                position: 'relative',
                                overflow: 'hidden'
                              }}
                            >
                              {isSelected && (
                                <div style={{
                                  position: 'absolute',
                                  top: '8px',
                                  right: '8px',
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '50%',
                                  background: selectionColor,
                                  color: 'white',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                  zIndex: 1
                                }}>
                                  ✓
                                </div>
                              )}
                              
                              {tb.grade && (
                                <div style={{
                                  position: 'absolute',
                                  top: '8px',
                                  left: '8px',
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  background: gradeBadgeColor,
                                  color: 'white',
                                  fontSize: '11px',
                                  fontWeight: 'bold',
                                  zIndex: 1
                                }}>
                                  {tb.grade}등급
                                </div>
                              )}
                              
                              <div style={{ 
                                padding: '20px 0',
                                background: 'linear-gradient(180deg, #fafafa 0%, #ffffff 100%)'
                              }}>
                                {tb.iconUrl ? (
                                  <img 
                                    alt={tb.name} 
                                    src={tb.iconUrl} 
                                    style={{ 
                                      width: '80px', 
                                      height: '80px', 
                                      objectFit: 'contain',
                                      margin: '0 auto'
                                    }} 
                                  />
                                ) : (
                                  <div style={{
                                    width: '80px',
                                    height: '80px',
                                    margin: '0 auto',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '48px',
                                    color: gradeBadgeColor
                                  }}>
                                    {selectedType === 'trophy' ? <TrophyOutlined /> : <SafetyOutlined />}
                                  </div>
                                )}
                              </div>
                              
                              <div style={{ padding: '12px 16px' }}>
                                <div style={{ 
                                  fontWeight: 600, 
                                  fontSize: '15px',
                                  marginBottom: '6px',
                                  color: '#1a1a1a'
                                }}>
                                  {tb.name}
                                </div>
                                {tb.description && (
                                  <div style={{ 
                                    fontSize: '12px',
                                    color: '#8c8c8c',
                                    lineHeight: '1.4',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    minHeight: '34px'
                                  }}>
                                    {tb.description}
                                  </div>
                                )}
                              </div>
                            </Card>
                          </Radio>
                        );
                      })}
                  </div>
                </Radio.Group>
              )}
            </div>
          </Form.Item>

          {isEditMode ? (
            // 수정 모드: 단일 수여대상자 표시 및 변경
            <Row gutter={24} className='mb-4'>
              <Col span={12}>
                <div style={{ marginBottom: '8px' }}>
                  <label className="text-sm font-medium" style={{ fontSize: '14px' }}>수여대상자 *</label>
                </div>
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    size="large"
                    value={selectedStudents.length > 0 ? selectedStudents[0].name : ''}
                    readOnly
                    placeholder="학생을 선택해주세요"
                    style={{ flex: 1 }}
                  />
                  <Button 
                    type="primary" 
                    size="large"
                    onClick={handleStudentModalOpen}
                  >
                    불러오기
                  </Button>
                </Space.Compact>
              </Col>
            </Row>
          ) : (
            // 등록 모드: 여러 명 선택 가능
            <>
              <Row gutter={24} className='mb-4' align='middle' style={{ marginTop: '40px' }}>
                <Col span={24}>
                  <div className="flex items-center gap-4">
                    <label className="text-sm font-medium" style={{ fontSize: '16px', fontWeight: 600 }}>수여대상자</label>
                    <Button 
                      type="primary" 
                      size="large" 
                      icon={<PlusOutlined />} 
                      onClick={handleStudentModalOpen}
                    >
                      학생 선택
                    </Button>
                  </div>
                </Col>
              </Row>

              {/* 선택된 학생 테이블 */}
              {selectedStudents.length > 0 && (
                <Row gutter={24}>
                  <Col span={24}>
                    <Title level={5}>선택된 학생</Title>
                    <div className='w-full max-h-[300px] overflow-y-auto'>
                    <StudentTable
                      data={selectedStudents}
                      showActions={true}
                      showSelectedColumns={true}
                      onDelete={(studentId: string) => {
                        setSelectedStudents(prev => prev.filter(student => student.id !== studentId));
                        message.success('학생이 제거되었습니다.');
                      }}
                    />
                    </div>
                  </Col>
                </Row>
              )}
            </>
          )}

        
          <Row gutter={24} className='mt-4'>
            <Col span={12}>
              <Form.Item
                label="수여자 메모"
                name="reason"
                rules={[
                  { required: true, message: '수여 사유를 입력해주세요' }
                ]}
              >
                <TextArea
                  placeholder="수여 사유를 상세히 입력해주세요"
                  rows={4}
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="수여일"
                name="grantDate"
                initialValue={dayjs()}
                rules={[
                  { required: true, message: '수여일을 선택해주세요' }
                ]}  
              >
               <DatePicker size="large" className='w-full' showTime />
              </Form.Item>
            </Col>
          </Row>
          <Divider />

          {/* 버튼 영역 */}
          <div style={{ textAlign: 'right', marginTop: 32 }}>
            <Space size="middle">
              <Button 
                onClick={handleCancel}
                size="large"
                style={{ height: 48, fontSize: 16, padding: '0 32px' }}
              >
                취소
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={saving}
                size="large"
                style={{ height: 48, fontSize: 16, padding: '0 32px' }}
              >
                {isEditMode ? '수정' : '수여'}
              </Button>
            </Space>
          </div>
        </Form>

        {/* 학생 선택 모달 */}
        <StudentSelectionModal
          open={studentModalVisible}
          onOk={handleStudentModalOk}
          onCancel={handleStudentModalCancel}
          availableStudents={availableStudents}
          title="학생 선택"
        />
      </div>
    </AdminLayout>
  );
};

export default AdminTrophyBadgeDetail; 